import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Truck, AlertCircle } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { useGeo, flagEmoji } from "@/lib/useGeo";
import { calculateShippingCost, MOQ } from "@/lib/shipping";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/seo/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, ...rest }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">
      {label}
    </span>
    <input
      {...rest}
      className="mt-1 block w-full h-11 px-3 border border-neutral-300 focus:border-black focus:outline-none text-sm"
    />
  </label>
);

export const Checkout = () => {
  const { cart } = useCart();
  const { user } = useAuth();
  const { geo } = useGeo();
  const { formatPrice } = useCurrency();
  const nav = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    zip: "",
    address: "",
    notes: "",
    clinic_name: "",
    save_account: false,
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Prefill from user account or geo
  useEffect(() => {
    setForm((f) => ({
      ...f,
      name: f.name || user?.name || user?.clinic_name || "",
      email: f.email || user?.email || "",
      phone: f.phone || user?.phone || "",
      country: f.country || user?.country || geo?.country || "",
      clinic_name: f.clinic_name || user?.clinic_name || "",
    }));
  }, [user, geo]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const shippingCost = useMemo(() => calculateShippingCost(cart.total_qty), [cart.total_qty]);
  const grandTotal = cart.subtotal + (shippingCost.cost || 0);

  // Pull session_id query for status polling on return
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) nav(`/order-confirmation?session_id=${sid}`, { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!cart.moq_reached) {
      setError(`Minimum order: ${MOQ} units. Add ${cart.moq_remaining} more.`);
      return;
    }
    if (form.save_account && form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        origin_url: window.location.origin,
        items: cart.items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        shipping: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          country: form.country,
          state: form.state,
          city: form.city,
          zip: form.zip,
          address: form.address,
          notes: form.notes,
        },
        save_account: form.save_account,
        password: form.save_account ? form.password : null,
        clinic_name: form.clinic_name,
      });
      window.location.href = data.url; // Redirect to Stripe
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong");
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Checkout · Discount Fillers" description="Secure B2B checkout via Stripe." path="/checkout" />
      <Header />
      <main className="pt-28 md:pt-32 pb-24 bg-neutral-50">
        <div className="max-w-[1200px] mx-auto px-6 grid md:grid-cols-[1fr_420px] gap-8" data-testid="checkout-page">
          {/* Form */}
          <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 md:p-8 space-y-6" data-testid="checkout-form">
            <header>
              <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 font-medium mb-2">
                Step 1 · Shipping
              </div>
              <h1 className="font-heading text-3xl font-black tracking-tight">Where do we ship?</h1>
              <p className="text-sm text-neutral-600 mt-1 font-light">
                No account required. We use your email only to send tracking and order updates.
              </p>
            </header>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name *" value={form.name} onChange={set("name")} required data-testid="ck-name" />
              <Field label="Clinic / Company" value={form.clinic_name} onChange={set("clinic_name")} data-testid="ck-clinic" />
              <Field label="Email *" type="email" value={form.email} onChange={set("email")} required data-testid="ck-email" />
              <Field label="Phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="ck-phone" />
              <Field
                label="Country *"
                value={form.country}
                onChange={set("country")}
                required
                placeholder={geo?.country ? `${flagEmoji(geo.countryCode)} ${geo.country}` : "Country"}
                data-testid="ck-country"
              />
              <Field label="State / Region" value={form.state} onChange={set("state")} data-testid="ck-state" />
              <Field label="City *" value={form.city} onChange={set("city")} required data-testid="ck-city" />
              <Field label="ZIP / Postal *" value={form.zip} onChange={set("zip")} required data-testid="ck-zip" />
            </div>
            <Field label="Street address *" value={form.address} onChange={set("address")} required data-testid="ck-address" />
            <label className="block">
              <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">
                Order notes (optional)
              </span>
              <textarea
                value={form.notes}
                onChange={set("notes")}
                rows={2}
                className="mt-1 block w-full px-3 py-2 border border-neutral-300 focus:border-black focus:outline-none text-sm"
                data-testid="ck-notes"
              />
            </label>

            {/* Optional registration */}
            {!user && (
              <div className="border-t border-neutral-200 pt-5 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.save_account}
                    onChange={set("save_account")}
                    className="mt-1"
                    data-testid="ck-save-account"
                  />
                  <span>
                    <div className="font-bold text-sm">Create my account (optional)</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      Save these details for next time + access wholesale verified pricing faster.
                    </div>
                  </span>
                </label>
                {form.save_account && (
                  <Field
                    label="Choose a password *"
                    type="password"
                    value={form.password}
                    onChange={set("password")}
                    minLength={6}
                    required
                    data-testid="ck-password"
                  />
                )}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 inline-flex items-center gap-2" data-testid="ck-error">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !cart.moq_reached || cart.items.length === 0}
              className="w-full h-12 bg-black text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:bg-neutral-300 disabled:cursor-not-allowed"
              data-testid="ck-pay-button"
            >
              <Lock className="w-4 h-4" />
              {submitting
                ? "Redirecting to Stripe…"
                : !cart.moq_reached
                ? `Add ${cart.moq_remaining} more items`
                : `Pay ${formatPrice(grandTotal)} securely`}
            </button>
            <div className="text-[11px] text-neutral-500 text-center">
              You will be redirected to Stripe to complete payment. We never see your card.
            </div>
          </form>

          {/* Order summary */}
          <aside className="bg-white border border-neutral-200 p-6 h-fit sticky top-32" data-testid="checkout-summary">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-medium mb-3">Order summary</div>
            <div className="space-y-3 max-h-72 overflow-auto pr-1">
              {cart.items.length === 0 ? (
                <div className="text-sm text-neutral-500">
                  Your cart is empty. <Link to="/" className="border-b border-black">Browse products</Link>
                </div>
              ) : cart.items.map((i) => (
                <div key={i.product_id} className="flex gap-3 text-sm">
                  <img src={i.image} alt={i.name} loading="lazy" className="w-12 h-12 object-cover bg-neutral-100" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{i.name}</div>
                    <div className="text-[11px] text-neutral-500">{formatPrice(i.unit_price)} × {i.qty}</div>
                  </div>
                  <div className="text-sm font-bold">{formatPrice(i.line_total)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-neutral-200 mt-4 pt-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-neutral-600">
                <span>Subtotal {cart.cart_discount_pct > 0 ? `(–${cart.cart_discount_pct}%)` : ""}</span>
                <span>{formatPrice(cart.subtotal)}</span>
              </div>
              <div className="flex justify-between text-neutral-600">
                <span className="inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Shipping (DHL)</span>
                <span>
                  {shippingCost.belowMoq ? "—" : formatPrice(shippingCost.cost)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-neutral-200 mt-2">
                <span className="font-bold text-sm">Total</span>
                <span className="font-heading text-2xl font-black tracking-tight">{formatPrice(grandTotal)}</span>
              </div>
              <div className="text-[10px] text-neutral-500 text-right">Charged in USD · MOQ {MOQ} units</div>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </>
  );
};
